import apiClient from './apiService';
import { API_URL,API_BASE_URL } from '../config';

class AuthService {
  constructor() {
    this.TOKEN_KEY = 'auth_token';
    this.USER_KEY = 'user_data';
    this._secret = API_BASE_URL || 'qwertyuiopasdfghjklzxcvbnm1234567890';
    this._salt = new TextEncoder().encode('metroleads_salt_v1');
  
  }

  // detect WebCrypto availability
  _hasWebCrypto() {
    return (typeof window !== 'undefined') && !!window.crypto && !!window.crypto.subtle;
  }

  // --- WebCrypto helpers (AES-GCM) ---
  async _deriveKey(secret) {
    if (!this._hasWebCrypto()) {
      throw new Error('WebCrypto not available');
    }
    const enc = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      enc.encode(secret),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );
    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: this._salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  async _encryptJSON(payload) {
    if (!this._hasWebCrypto()) {
      return btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    }

    const key = await this._deriveKey(this._secret);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const enc = new TextEncoder();
    const ct = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      enc.encode(JSON.stringify(payload))
    );
    return `${this._bufToBase64(iv)}:${this._bufToBase64(ct)}`;
  }

  async _decryptJSON(payloadStr) {
    if (!payloadStr) return null;

    // if WebCrypto is not available, assume base64-encoded JSON
    if (!this._hasWebCrypto()) {
      try {
        const jsonStr = decodeURIComponent(escape(atob(payloadStr)));
        return JSON.parse(jsonStr);
      } catch (e) {
        // If it's plain JSON stored earlier, try parsing directly
        try { return JSON.parse(payloadStr); } catch (_) { return null; }
      }
    }

    const [ivB64, ctB64] = payloadStr.split(':');
    if (!ivB64 || !ctB64) {
      try {
        const jsonStr = decodeURIComponent(escape(atob(payloadStr)));
        return JSON.parse(jsonStr);
      } catch (e) {
        return null;
      }
    }
    const key = await this._deriveKey(this._secret);
    const iv = new Uint8Array(this._base64ToBuf(ivB64));
    const ct = this._base64ToBuf(ctB64);
    const dec = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ct
    );
    const decStr = new TextDecoder().decode(dec);
    return JSON.parse(decStr);
  }
  // Get
  getUser() {
    const userData = localStorage.getItem(this.USER_KEY);
    if (!userData) return null;

    // 1) plain JSON string
    try {
      if (userData.trim().startsWith('{')) {
        return JSON.parse(userData);
      }
    } catch (e) {
      // fall through to other checks
    }

    // 2) base64-encoded JSON (fallback used when WebCrypto not available)
    const cleaned = userData.replace(/\s+/g, '');
    const base64Like = /^[A-Za-z0-9+/=]+$/.test(cleaned) && cleaned.length > 20;
    if (base64Like) {
      try {
        // decode base64 -> utf8 string -> parse JSON
        const jsonStr = decodeURIComponent(escape(atob(cleaned)));
        return JSON.parse(jsonStr);
      } catch (e) {
        // fall through
      }
    }

    // 3) WebCrypto-encrypted format "ivBase64:ctBase64"
    // cannot decrypt synchronously here; return null so callers can call async decrypt helper
    if (userData.includes(':')) {
      // consumers that need decrypted object should call authService._decryptJSON(userData)
      return null;
    }

    // fallback
    return null;
  }
  // Generate base64 encoded token from email and UUID
  generateToken(email, uuid) {
    const tokenString = `${email}:${uuid}`;
   console.log(tokenString);
    return btoa(unescape(encodeURIComponent(tokenString)))
    //return btoa(tokenString);
  }

  // Login with email and password using GET method
  async login(email, password) {
    try {
      const response = await apiClient.get(`${API_URL}/auth/issue_token`, {
        params: {
          username: email,
          password: password
        }
      });

      if (response && response.data) {
        const token = this.generateToken(email, response.data);
        
        // Store token and user data
        localStorage.setItem(this.TOKEN_KEY, token);
        localStorage.setItem(this.USER_KEY, JSON.stringify({
          email,
          uuid: response.data
        }));

         // fetch profile (/me) to extract required fields and store encrypted
        let profile = null;
        try {
          profile = await this.getUserDetails();
          //console.log('Fetched user profile:', profile);
          
        if (profile) {
          localStorage.setItem('company_id', profile.company_id || '' );
          const encryptedProfile = await this._encryptJSON(profile);
          localStorage.setItem(this.USER_KEY, encryptedProfile);
        }

        } catch (e) {
          console.error('Failed to fetch user profile:', e);
          profile = { email, uuid: response.data };
        }

        return { success: true, token, data: response };
      }

      return { success: false, message: 'Invalid response from server' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed. Please try again.'
      };
    }
  }

  // Get user details using the token
  async getUserDetails() {
    const token = this.getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await apiClient.post(
        `${API_URL}/me`
      );

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch user details');
    }
  }
  

  // Get stored token
  getToken() {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // Get stored user data (robust: plain JSON, base64-JSON, or WebCrypto "iv:ct")
  getUser() {
    try {
      const raw = localStorage.getItem(this.USER_KEY);
      if (!raw) return null;
      const s = String(raw).trim();

      // 1) plain JSON
      if (s.startsWith('{') || s.startsWith('[')) {
        try { return JSON.parse(s); } catch (_) { /* fallthrough */ }
      }

      // 2) WebCrypto encrypted format "iv:ct" -> cannot decrypt synchronously
      if (s.includes(':') && s.split(':').length === 2) {
        return null;
      }

      // 3) base64 (standard or url-safe) -> decode to UTF-8 and parse JSON
      const base64Like = /^[A-Za-z0-9+/=_-]+$/.test(s) && s.length > 16;
      if (base64Like) {
        try {
          const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
          const binary = atob(b64);
          // decode binary to UTF8 safely
          let decoded = '';
          for (let i = 0; i < binary.length; i++) decoded += String.fromCharCode(binary.charCodeAt(i));
          // attempt to parse JSON
          try { return JSON.parse(decodeURIComponent(escape(decoded))); } catch (_) {
            // fallback: maybe decoded already is JSON-like without escapes
            try { return JSON.parse(decoded); } catch (_) { return null; }
          }
        } catch (err) {
          console.warn('authService.getUser: base64 decode/parse failed', err);
          return null;
        }
      }

      // final fallback
      return null;
    } catch (err) {
      console.warn('authService.getUser: unexpected error', err);
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getToken();
  }

  // Logout
  logout() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }
}

export default new AuthService();